'use strict';

import {
  applyTransferPoll,
  createSessionProgress,
  isUploadFinished,
} from './uploadProgress';

const IDLE = { progress: null, phase: 'idle', message: '' };

let snapshot = IDLE;
let listeners = new Set();
let timer = null;
let generation = 0;

function emit(next) {
  snapshot = next;
  listeners.forEach((listener) => listener(snapshot));
}

export function getSessionTransfer() {
  return snapshot;
}

export function subscribeSessionTransfer(listener) {
  listeners.add(listener);
  listener(snapshot);
  return () => {
    listeners.delete(listener);
  };
}

export function recorderHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: 'Bearer ' + token } : {};
}

function clearTimer() {
  if (timer != null) {
    clearInterval(timer);
    timer = null;
  }
}

export function cancelCameraDownload(recordingUrl) {
  const base = recordingUrl != null ? recordingUrl : (window.RECORDING_URL || '');
  return Promise.resolve()
    .then(() => fetch(`${base}/api/cancel-download`, {
      method: 'POST',
      headers: recorderHeaders(),
    }))
    .catch((err) => {
      console.error('Failed to cancel camera download:', err);
    });
}

export function stopSessionTransfer(options) {
  generation += 1;
  clearTimer();
  emit(IDLE);
  if (options && options.cancelDownload) {
    cancelCameraDownload(options.recordingUrl);
  }
}

export function acknowledgeSessionTransferMessage() {
  if (!snapshot.message) return;
  emit({ ...snapshot, message: '' });
}

function readJson(response) {
  if (!response || typeof response.json !== 'function') return Promise.resolve({});
  return response.json();
}

function stillRunning(run) {
  return generation === run && snapshot.phase === 'running';
}

export function startSessionTransfer({ trialCount, evowalkEnabled, recordingUrl } = {}) {
  const run = generation + 1;
  generation = run;
  clearTimer();
  const base = recordingUrl != null ? recordingUrl : '';
  emit({
    progress: createSessionProgress({ trialCount, cameraCount: 0, evowalkEnabled }),
    phase: 'running',
    message: '',
  });

  fetch(`${base}/api/finish-session`, { headers: recorderHeaders() })
    .then(readJson)
    .then((res) => {
      if (!stillRunning(run)) return null;
      const message = res && typeof res.message === 'string' ? res.message : '';
      if (message) emit({ ...snapshot, message });
      return fetch(`${base}/api/total-cameras`, { headers: recorderHeaders() }).then(readJson);
    })
    .then((data) => {
      if (!data || !stillRunning(run)) return;
      emit({
        progress: createSessionProgress({
          trialCount,
          cameraCount: data.tot_cams,
          evowalkEnabled,
        }),
        phase: 'running',
        message: snapshot.message,
      });
      timer = setInterval(() => {
        Promise.all([
          fetch(`${base}/api/update-download`, { headers: recorderHeaders() }).then(readJson),
          fetch(`${base}/api/update-upload`, { headers: recorderHeaders() }).then(readJson),
        ])
          .then(([downloadData, uploadData]) => {
            if (!stillRunning(run)) return;
            const next = applyTransferPoll(snapshot.progress, downloadData, uploadData);
            if (isUploadFinished(next)) {
              clearTimer();
              generation += 1;
              emit({ progress: null, phase: 'complete', message: '' });
              return;
            }
            emit({ progress: next, phase: 'running', message: '' });
          })
          .catch((error) => {
            console.error('Error fetching upload progress:', error);
            if (!stillRunning(run)) return;
            clearTimer();
            generation += 1;
            emit({ progress: null, phase: 'error', message: '' });
          });
      }, 3000);
    })
    .catch((err) => {
      console.error(err);
      if (!stillRunning(run)) return;
      clearTimer();
      generation += 1;
      const errorMessage = (err && err.message) || 'An unknown error occurred.';
      emit({
        progress: null,
        phase: 'error',
        message: `Could not process the videos: ${errorMessage}`,
      });
    });
}
